import { useMemo, useEffect, useReducer, useState, useCallback } from 'react';
import { FilterChipsBase as FilterChips, IFilterChip } from '@glu/data-components';
import { useComposedGridContext } from 'ComposedGrid/ComposedGridContext';
import {
  getDateFilterChip,
  getNumberFilterChip,
  getSetFilterChip,
  defaultGetCustomFilterChip,
  getTextFilterChip,
  TFilter,
  TCreateFilter,
} from './utils/getFilterChip';

export interface IFilterChipsAgGridProps {
  /**
   * A function to handle showing a filter chip for a custom filter.
   * The data here is defined by getModel when you define the custom filter. There is an example
   * of how to use this in the storybook.
   */
  getCustomFilterChip?: typeof defaultGetCustomFilterChip;
}

export const useFilterChipsAgGrid = ({
  getCustomFilterChip = defaultGetCustomFilterChip,
}: IFilterChipsAgGridProps) => {
  const { gridRef } = useComposedGridContext();
  const [hidden, setHidden] = useState(false);
  const [, forceUpdate] = useReducer((i) => i + 1, 0);
  const filterModel: Record<string, TFilter> = gridRef.current.api.getFilterModel();
  const filterChips = useMemo(() => {
    return Object.entries(filterModel)
      .map<IFilterChip | IFilterChip[]>(([field, filterModelInstance]) => {
        const column = gridRef.current.columnApi.getColumn(field)!;
        const columnDisplayName = gridRef.current.columnApi.getDisplayNameForColumn(column, null);
        switch (filterModelInstance.filterType) {
          case 'date': {
            return getDateFilterChip({
              field,
              columnDisplayName,
              column,
              filterModel: filterModelInstance,
            });
          }
          case 'set': {
            return getSetFilterChip({
              field,
              columnDisplayName,
              column,
              filterModel: filterModelInstance,
            });
          }
          case 'number': {
            return getNumberFilterChip({
              field,
              columnDisplayName,
              column,
              filterModel: filterModelInstance,
            });
          }

          case 'text': {
            return getTextFilterChip({
              field,
              columnDisplayName,
              column,
              filterModel: filterModelInstance,
            });
          }
          default:
            return getCustomFilterChip({
              field,
              columnDisplayName,
              column,
              filterModel: filterModelInstance,
            });
        }
      })
      .flat();
  }, [filterModel, gridRef, getCustomFilterChip]);

  const toggle = useCallback(() => {
    setHidden((h) => !h);
  }, []);

  const onFilterRemove = useCallback(
    (filterChipToRemove: IFilterChip) => {
      const currentFilterModel = gridRef.current.api.getFilterModel();
      const modelForChip = currentFilterModel[filterChipToRemove.field];
      const filterInstance = gridRef.current.api.getFilterInstance(filterChipToRemove.field)!;
      if (filterChipToRemove) {
        if (modelForChip.filterType === 'set') {
          const { criteriaDisplay } = filterChipToRemove;
          const currentModel: TCreateFilter<'set'> = filterInstance.getModel();
          const filteredValues = currentModel.values.filter((n) => n !== criteriaDisplay);

          filterInstance.setModel(
            filteredValues.length > 0
              ? {
                  ...currentModel,
                  values: filteredValues,
                }
              : null
          );
        } else {
          filterInstance.setModel(null);
        }
      }
      gridRef.current.api.onFilterChanged();
    },
    [gridRef]
  );

  /**
   * Keeps ag-grid as the primary source of truth without having to duplicate state
   * The forceUpdate is unfortunate, but better than the alternative of duplicating state
   */
  useEffect(() => {
    const currRef = gridRef.current;
    const onAgGridPageChange = () => {
      forceUpdate();
    };
    if (currRef.api) {
      currRef.api.addEventListener('filterChanged', onAgGridPageChange);
    }
    return () => {
      currRef.api.removeEventListener('filterChanged', onAgGridPageChange);
    };
  }, [gridRef]);

  return useMemo(
    () => ({
      filterChips,
      hidden,
      onFilterRemove,
      toggle,
    }),
    [filterChips, hidden, onFilterRemove, toggle]
  );
};

const FilterChipsAgGrid = ({ getCustomFilterChip }: IFilterChipsAgGridProps) => {
  const propsFromHook = useFilterChipsAgGrid({ getCustomFilterChip });
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <FilterChips {...propsFromHook} />;
};

export default FilterChipsAgGrid;
